import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import Cookies from "js-cookie";
import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import AppContext from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";
import translations from "../../components/common/Translations";
import { saveBulkAssignTask, saveBulkAssignTaskByDept, saveBulkTask, lookupAccountDetails, multiassignTasks } from "../../service/Task";
import CommonButton from "../../components/common/CommonButton";
import Tooltip from "../../components/common/Tooltip";
import ReactSelector from "../../components/common/CommonReactSelector";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import { filterAgentsByCurrentUserGroups } from "../../components/task/GroupAssignParts";
import { hasScreenAccess } from "../../utils/permissions";
import { components } from "react-select";

import { ArrowLeft, ChevronDown, Loader2 } from "lucide-react";
import { X } from "lucide-react";
import { canReassignTask } from "../../utils/taskReassignRules";

const Placeholder = (props) => <components.Placeholder {...props} />;

export default function BulkUploadModal({ setBulkUploadOpen, bulkUploadOpen, allowUpdateIfExists, inline = false, isReassign = false }) {
  const context = useContext(AppContext);
  const [fieldAgents, setFieldAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [accountNumbers, setAccountNumbers] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isCreateAssignLoading, setIsCreateAssignLoading] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState("High_Priority");
  // Reassign flow is a two-step modal: step 1 = input, step 2 = new/existing split.
  const [step, setStep] = useState(1);
  const [newAccounts, setNewAccounts] = useState([]);
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [expandedExisting, setExpandedExisting] = useState(null);
  const [isLookupLoading, setIsLookupLoading] = useState(false);
  const [isReassignLoading, setIsReassignLoading] = useState(false);
  const dropdownRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const navigate = useNavigate();
  

  const { theme, langMode, activityId, projectId } = context;
  // Task creation is gated behind the TASK_MANAGEMENT "create" permission.
  const canCreateTask = hasScreenAccess("TASK_MANAGEMENT", "canCreate");
  // Bulk Priority Update is gated behind its own TASK_MANAGEMENT permission. Users who
  // only hold that permission land directly in priority mode.
  const canBulkPriorityUpdate = hasScreenAccess("TASK_MANAGEMENT", "canBulkPriorityUpdate");
  const [bulkMode, setBulkMode] = useState(canCreateTask ? "create" : "priority");
  const isPriorityMode = canBulkPriorityUpdate && bulkMode === "priority";
  const workflowName = localStorage.getItem("workflowName");
  const [searchTerm, setSearchTerm] = useState("");

  const handleGoBack = () => {
    const navigationPath =
      Cookies.get("viewmode") === "map" ? "/maptask" : "/task-manage";
    navigate(navigationPath);
    Cookies.remove("viewmode");
  };

  // Clear everything the user entered/selected (both the step-1 inputs and the
  // step-2 split) so a fresh flow starts clean after a successful final submit.
  const resetBulkState = () => {
    setStep(1);
    setAccountNumbers([]);
    setSelectedAgents([]);
    setInputValue("");
    setNewAccounts([]);
    setExistingAccounts([]);
    setExpandedExisting(null);
  };

  // Reset the reassign step whenever the modal is closed/reopened.
  useEffect(() => {
    if (!bulkUploadOpen) {
      setStep(1);
      setNewAccounts([]);
      setExistingAccounts([]);
      setExpandedExisting(null);
    }
  }, [bulkUploadOpen]);

  // Look up the entered account numbers, splitting them into existing accounts
  // (with their current assignees) and brand-new accounts, then show step 2.
  const handleReassignSubmit = async () => {
    if (accountNumbers.length === 0) {
      toast.error(translations[langMode].pleaseenteraccnumber);
      return;
    }
    if (!activityId) {
      toast.error(translations[langMode].plzselectactivity);
      return;
    }
    try {
      setIsLookupLoading(true);
      const response = await lookupAccountDetails({
        projectId: Number(projectId),
        accountNumbers,
        workflowId: activityId,
      });
      setIsLookupLoading(false);
      if (response?.header?.code === 600) {
        const value = response?.body?.value || {};
        setExistingAccounts(Array.isArray(value.existingAccounts) ? value.existingAccounts : []);
        setNewAccounts(Array.isArray(value.newAccounts) ? value.newAccounts : []);
        setExpandedExisting(null);
        setStep(2);
      } else {
        toast.error(
          response?.body?.value ?? response?.body?.error ?? response?.header?.message ?? (translations[langMode]?.buLookupFailed ?? "Lookup failed.")
        );
      }
    } catch (error) {
      setIsLookupLoading(false);
      console.error("Error looking up account details:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.message;
      toast.error(backendMessage || (translations[langMode]?.buUnexpectedError ?? "An unexpected error occurred."));
    }
  };

  // "Create and Assign": send ALL entered accounts (both new and already-existing)
  // to the create-and-assign endpoint with the chosen priority.
  const handleCreateAndAssign = async () => {
    const allAccountNumbers = [
      ...newAccounts,
      ...existingAccounts.map((item) => item.accountNo),
    ];
    if (allAccountNumbers.length === 0) {
      toast.error(translations[langMode].pleaseenteraccnumber);
      return;
    }
    if (selectedAgents.length === 0) {
      toast.error(translations[langMode].plzselectedforilfagrntoasing);
      return;
    }
    try {
      setIsCreateAssignLoading(true);
      const response = await saveBulkAssignTaskByDept({
        accountNumbers: allAccountNumbers,
        fieldAgentIds: selectedAgents,
        workflowId: activityId,
        workflowName,
        allowUpdateIfExists,
        priority: selectedPriority,
      });
      setIsCreateAssignLoading(false);
      if (response === null) {
        toast.error(translations[langMode].errorfiledownloaded);
      } else if (response.header.code === 600) {
        toast.success(translations[langMode].taskcreatedsuc);
        resetBulkState();
        setBulkUploadOpen(false);
        handleGoBack();
      } else {
        toast.error(response.body.value);
      }
    } catch (error) {
      setIsCreateAssignLoading(false);
      console.error("Error in handleCreateAndAssign:", error);
      toast.error(translations[langMode]?.buUnexpectedError ?? "An unexpected error occurred.");
    }
  };

  // Existing accounts: re-assign their tasks to the selected field agents.
  // The bulk flow sends the account numbers (as `accountNumbers`) to v1/task/re-assign.
  const handleReassignExisting = async () => {
    if (existingAccounts.length === 0) {
      toast.error(translations[langMode].pleaseenteraccnumber);
      return;
    }
    if (selectedAgents.length === 0) {
      toast.error(translations[langMode].plzselectedforilfagrntoasing);
      return;
    }
    // Completed tasks can only be re-assigned when they finished unsuccessfully
    // in an allowed workflow -- skip the rest instead of failing the whole batch.
    const reassignableAccounts = existingAccounts.filter((item) => canReassignTask(item, activityId));
    if (reassignableAccounts.length < existingAccounts.length) {
      toast.warn(translations[langMode].completedTaskReassignNotAllowed || "Only unsuccessful completed tasks can be reassigned.");
    }
    if (reassignableAccounts.length === 0) return;
    try {
      setIsReassignLoading(true);
      const existingAccountNos = reassignableAccounts.map((item) => item.accountNo);
      const response = await multiassignTasks(existingAccountNos, selectedAgents, activityId, langMode, null, true);
      setIsReassignLoading(false);
      if (!response) return; // validation error already surfaced by the service
      if (response?.header?.code === 600) {
        toast.success(response?.body?.value ?? translations[langMode].taskcreatedsuc);
        resetBulkState();
        setBulkUploadOpen(false);
        handleGoBack();
      } else {
        toast.error(
          response?.body?.value ?? response?.body?.error ?? response?.header?.message
        );
      }
    } catch (error) {
      setIsReassignLoading(false);
      console.error("Error in handleReassignExisting:", error);
      toast.error(translations[langMode]?.buUnexpectedError ?? "An unexpected error occurred.");
    }
  };

  useEffect(() => {
    console.log("activity id: ", activityId)
    if (bulkUploadOpen && activityId) {
      const token = Cookies.get("accessToken");

      // resolved params issue by harshit vachhani
      if (!activityId) {
        return;
      }

      axios
        .get(
          `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/user/field-agents-by-workflow?workflowId=${activityId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        .then(async (response) => {
          const rawAgents = response?.data?.body?.value;
          if (rawAgents) {
            // Only show field agents who belong to a group assigned to this user
            // (blank group list => show all).
            setFieldAgents(await filterAgentsByCurrentUserGroups(rawAgents));
          }
        })
        .catch(console.error);
    }
  }, [bulkUploadOpen, activityId]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(""); // Clear search term when dropdown closes
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  const handlePasteExcel = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const values = pasted
      .split(/[\n,\t\s]+/)
      .map((line) => line.trim())
      .filter((line) => line !== "");
    if (isPriorityMode) {
      const ignored = values.length - new Set(values.filter((val) => !accountNumbers.includes(val))).size;
      if (ignored > 0) {
        toast.warn((translations[langMode]?.buDuplicatesIgnored ?? "{n} duplicate account number(s) ignored.").replace("{n}", ignored));
      }
    }
    setAccountNumbers((prev) => {
      const uniqueNewValues = values.filter((val) => !prev.includes(val));
      return [...prev, ...uniqueNewValues];
    });
  };

  const handleManualEntryKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = inputValue.trim();
      if (val) {
        setAccountNumbers((prev) => {
          if (!prev.includes(val)) {
            return [...prev, val];
          }
          return prev;
        });
        setInputValue("");
      }
    } else if (e.key === "Backspace" && !inputValue && accountNumbers.length > 0) {
      setAccountNumbers((prev) => prev.slice(0, -1));
    }
  };

  const handleBulkAssignSaveTask = async () => {
    console.log('clicking...')
    if (accountNumbers.length === 0)
      toast.error(translations[langMode].pleaseenteraccnumber);
    else if (!workflowName)
      toast.error(translations[langMode].plzselectactivity);
    else if (!activityId)
      toast.error(translations[langMode].plzselectactivity);
    else if (selectedAgents.length === 0)
      toast.error(translations[langMode].plzselectedforilfagrntoasing);
    else {
      const data = {
        accountNumbers,
        fieldAgentIds: selectedAgents,
        workflowId: activityId,
        workflowName,
        allowUpdateIfExists,
        priority: selectedPriority
      }
      try {
        setIsCreateAssignLoading(true)
        const response = await saveBulkAssignTaskByDept(data);
        setIsCreateAssignLoading(false)
        if (response === null) {
          toast.error(translations[langMode].errorfiledownloaded);
        } else if (response.header.code === 600) {
          setBulkUploadOpen(false);
          toast.success(translations[langMode].taskcreatedsuc);
          handleGoBack();
        } else {
          toast.error(response.body.value);
        }
      } catch (error) {
        
        setIsCreateAssignLoading(false)
        console.error("Error in handleSaveTask:", error);
        toast.error(translations[langMode]?.buUnexpectedError ?? "An unexpected error occurred.");
      }
    }
  };

  const handleBulkSaveTask = async () => {
    if (
      accountNumbers.length === 0 ||
      !workflowName ||
      !activityId
    ) {
      toast.error(translations[langMode].noFieldsAvailable);
    } else {
      try {
        setIsCreateLoading(true)
        const data = {
          accountNumbers,
          fieldAgentIds: selectedAgents,
          workflowId: activityId,
          workflowName,
          allowUpdateIfExists,
          priority: selectedPriority
        }
        const response = await saveBulkTask(data);
        setIsCreateLoading(false)
        if (response === null) {
          toast.error(translations[langMode].errorfiledownloaded);
        } else if (response.header.code === 600) {
          setBulkUploadOpen(false);
          toast.success(translations[langMode].taskcreatedsuc);
          handleGoBack();
        } else {
          toast.error(response.body.value);
        }
      } catch (error) {
        setIsCreateLoading(false)
        console.error("Error in handleSaveTask:", error);
        toast.error(translations[langMode]?.buUnexpectedError ?? "An unexpected error occurred.");
      }
    }
  };

  // Bulk Priority Update: same update-bulk-task endpoint and response handling as the
  // "Create Tasks" button, but always with allowUpdateIfExists so existing accounts are
  // updated. Invalid/unavailable accounts come back from the backend as the usual error
  // sheet or message.
  const handleBulkPriorityUpdate = async () => {
    if (accountNumbers.length === 0) {
      toast.error(translations[langMode].pleaseenteraccnumber);
    } else if (!workflowName || !activityId) {
      toast.error(translations[langMode].plzselectactivity);
    } else if (!selectedPriority) {
      toast.error(translations[langMode]?.buSelectPriority ?? "Please select a priority.");
    } else {
      try {
        setIsCreateLoading(true);
        const response = await saveBulkTask({
          accountNumbers,
          fieldAgentIds: selectedAgents,
          workflowId: activityId,
          workflowName,
          allowUpdateIfExists: true,
          priority: selectedPriority,
        });
        setIsCreateLoading(false);
        if (response === null) {
          toast.error(translations[langMode].errorfiledownloaded);
        } else if (response.header.code === 600) {
          setBulkUploadOpen(false);
          toast.success(
            typeof response?.body?.value === "string" && response.body.value
              ? response.body.value
              : (translations[langMode]?.buPriorityUpdated ?? "Priority updated for {n} account(s).").replace("{n}", accountNumbers.length)
          );
          handleGoBack();
        } else {
          toast.error(response?.body?.value ?? response?.header?.message ?? (translations[langMode]?.buPriorityUpdateFailed ?? "Priority update failed."));
        }
      } catch (error) {
        setIsCreateLoading(false);
        console.error("Error in handleBulkPriorityUpdate:", error);
        toast.error(translations[langMode]?.buUnexpectedError ?? "An unexpected error occurred.");
      }
    }
  };

  // Update Priority and Assign: same create-assign-bulk-task endpoint as "Create and
  // Assign", with allowUpdateIfExists so existing accounts get the new priority.
  const handleBulkPriorityUpdateAndAssign = async () => {
    if (accountNumbers.length === 0) {
      toast.error(translations[langMode].pleaseenteraccnumber);
    } else if (!workflowName || !activityId) {
      toast.error(translations[langMode].plzselectactivity);
    } else if (selectedAgents.length === 0) {
      toast.error(translations[langMode].plzselectedforilfagrntoasing);
    } else if (!selectedPriority) {
      toast.error(translations[langMode]?.buSelectPriority ?? "Please select a priority.");
    } else {
      try {
        setIsCreateAssignLoading(true);
        const response = await saveBulkAssignTaskByDept({
          accountNumbers,
          fieldAgentIds: selectedAgents,
          workflowId: activityId,
          workflowName,
          allowUpdateIfExists: true,
          priority: selectedPriority,
        });
        setIsCreateAssignLoading(false);
        if (response === null) {
          toast.error(translations[langMode].errorfiledownloaded);
        } else if (response.header.code === 600) {
          setBulkUploadOpen(false);
          toast.success(
            typeof response?.body?.value === "string" && response.body.value
              ? response.body.value
              : (translations[langMode]?.buPriorityUpdated ?? "Priority updated for {n} account(s).").replace("{n}", accountNumbers.length)
          );
          handleGoBack();
        } else {
          toast.error(response?.body?.value ?? response?.header?.message ?? (translations[langMode]?.buPriorityUpdateFailed ?? "Priority update failed."));
        }
      } catch (error) {
        setIsCreateAssignLoading(false);
        console.error("Error in handleBulkPriorityUpdateAndAssign:", error);
        toast.error(translations[langMode]?.buUnexpectedError ?? "An unexpected error occurred.");
      }
    }
  };

  const filteredFieldAgents = fieldAgents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const agentOptions = Array.isArray(fieldAgents)
    ? fieldAgents.map((agent) => ({
      value: agent.userId,
      label: agent.name,
    }))
    : [];

  const selectedOptions = agentOptions.filter((option) =>
    selectedAgents.includes(option.value)
  );

  const handleAgentSelection = (selected) => {
    const selectedIds = selected ? selected.map((option) => option.value) : [];
    setSelectedAgents(selectedIds);
  };

  const priorityOptions = [
    { value: "Low_Priority", label: translations[langMode].lowPriority },
    { value: "Medium_Priority", label: translations[langMode].mediumPriority },
    { value: "High_Priority", label: translations[langMode].highPriority },
  ];

  const shortPriorityOptions = [
    { value: "Low_Priority", label: translations[langMode]?.low ?? "Low" },
    { value: "Medium_Priority", label: translations[langMode]?.medium ?? "Medium" },
    { value: "High_Priority", label: translations[langMode]?.high ?? "High" },
  ];

  if (inline && !isReassign) {
    return (
      <div className="max-w-[1400px] w-full mx-auto px-5 pt-6 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <section className="min-w-0 rounded-xl border border-slate-100 bg-white p-5 flex flex-col justify-between">
            <div>
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#236DB4] text-[10px] font-bold text-white">1</span>
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">{translations[langMode]?.buAccountNumbers ?? "Account Numbers"}</h3>
              </div>

              <fieldset className="min-w-0 w-full max-w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 focus-within:border-[#236DB4] focus-within:ring-2 focus-within:ring-[#236DB4]/15 transition-all duration-200">
                <legend className="px-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {translations[langMode]?.buAccountNo ?? "Account No"} <span className="text-red-500">*</span>
                </legend>
                <div className="flex min-h-[140px] max-h-[190px] min-w-0 max-w-full flex-wrap items-start content-start gap-2 overflow-y-auto overflow-x-hidden pb-1">
                  {accountNumbers.map((num, index) => (
                    <span
                      key={index}
                      className="inline-flex max-w-full min-w-0 shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700 shadow-sm"
                    >
                      <span className="min-w-0 truncate">{num}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setAccountNumbers((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        className="text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    placeholder={accountNumbers.length === 0 ? (translations[langMode]?.buTypeAccountPlaceholder ?? "Type account number and press Enter") : ""}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleManualEntryKeyDown}
                    onPaste={handlePasteExcel}
                    onBlur={() => {
                      const val = inputValue.trim();
                      if (val && !accountNumbers.includes(val)) {
                        setAccountNumbers((prev) => [...prev, val]);
                        setInputValue("");
                      }
                    }}
                    className="min-w-0 flex-[1_1_120px] max-w-full border-none bg-transparent p-1 text-sm text-slate-700 outline-none focus:ring-0"
                  />
                </div>
              </fieldset>
            </div>
            
            <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
              <span>{translations[langMode]?.buTypeAndEnterHint ?? "Type and press Enter, or paste a list of accounts."}</span>
              <span className="font-medium text-slate-500">{(translations[langMode]?.buDetected ?? "{n} detected").replace("{n}", accountNumbers.length)}</span>
            </div>
          </section>

          <section className="rounded-xl border border-slate-100 bg-white p-5 flex flex-col justify-between">
            <div>
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#236DB4] text-[10px] font-bold text-white">2</span>
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">{translations[langMode]?.buAssignmentPriority ?? "Assignment & Priority"}</h3>
              </div>
              {canBulkPriorityUpdate && canCreateTask && (
                <div className="mb-4 inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1" data-testid="bulk-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setBulkMode("create")}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${bulkMode === "create" ? "bg-white text-[#236DB4] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    {translations[langMode]?.buCreateTasks ?? "Create Tasks"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkMode("priority")}
                    data-testid="bulk-priority-update-mode"
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${bulkMode === "priority" ? "bg-white text-[#236DB4] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    {translations[langMode]?.bulkPriorityUpdate ?? "Bulk Priority Update"}
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div data-testid="bulk-upload-field-agent-select">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    {translations[langMode].selectFieldAget}
                  </label>
                  <ReactSelector
                    isMulti
                    options={agentOptions}
                    value={selectedOptions}
                    onChange={handleAgentSelection}
                    height="38px"
                    isClearable
                    menuPortalTarget={document.body}
                    placeholder={translations[langMode].selectFieldAget}
                    components={{ Placeholder }}
                  />
                </div>
                <div data-testid="bulk-upload-priority-select">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    {translations[langMode].taskpriority}
                  </label>
                  <ReactSelector
                    value={priorityOptions.find((option) => option.value === selectedPriority) || null}
                    options={priorityOptions}
                    onChange={(selected) => setSelectedPriority(selected?.value || "")}
                    height="38px"
                    menuPortalTarget={document.body}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    {translations[langMode].totalcount}
                  </label>
                  <input
                    type="text"
                    disabled
                    value={accountNumbers.length}
                    className="flex h-[38px] w-full items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-700 cursor-not-allowed"
                    data-testid="bulk-upload-total-count"
                  />
                  <p className="mt-1.5 text-xs text-slate-400">{translations[langMode]?.buTotalAccountsHint ?? "Total account numbers added above."}</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-8 flex items-center justify-end gap-3 border-t border-slate-100 bg-white px-5 py-4">
          <CommonButton colorIntent="danger"
            onClick={() => setBulkUploadOpen(false)}
            variant="danger"
            data-testid="bulk-upload-cancel-button"
            tooltipText={translations[langMode].gobacktheprivious || "Cancel and go back"}
            tooltipPosition="top"
          >
            {translations[langMode].cancel || "Cancel"}
          </CommonButton>
          {isPriorityMode ? (
            <>
            <Tooltip text={translations[langMode]?.buUpdatePriorityTooltip ?? "Update priority of the entered accounts"} position="top">
            <button
              type="button"
              onClick={handleBulkPriorityUpdate}
              disabled={isCreateLoading || isCreateAssignLoading}
              className="h-9 rounded-lg bg-[#236DB4] px-5 text-sm font-semibold text-white hover:bg-[#1d5992] disabled:opacity-60"
              data-testid="bulk-priority-update-button"
            >
              {isCreateLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {translations[langMode]?.buUpdatingPriority ?? "Updating priority..."}
                </span>
              ) : (
                translations[langMode]?.buUpdatePriority ?? "Update Priority"
              )}
            </button>
            </Tooltip>
            <CommonButton colorIntent="success"
              onClick={handleBulkPriorityUpdateAndAssign}
              variant="success"
              disabled={isCreateLoading || isCreateAssignLoading}
              tooltipText={translations[langMode]?.buUpdatePriorityAssignTooltip ?? "Update priority and assign to the selected field agents"}
              tooltipPosition="top"
              data-testid="bulk-priority-update-assign-button"
            >
              {isCreateAssignLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {translations[langMode]?.buUpdatingPriority ?? "Updating priority..."}
                </span>
              ) : (
                translations[langMode]?.buUpdatePriorityAndAssign ?? "Update Priority and Assign"
              )}
            </CommonButton>
            </>
          ) : (
          <>
          <Tooltip text={translations[langMode].createamanualtask ?? "Create the tasks for the entered accounts"} position="top">
          <button
            type="button"
            onClick={handleBulkSaveTask}
            disabled={isCreateLoading || isCreateAssignLoading}
            className="h-9 rounded-lg bg-[#236DB4] px-5 text-sm font-semibold text-white hover:bg-[#1d5992] disabled:opacity-60"
            data-testid="bulk-upload-save-button"
          >
            {isCreateLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {translations[langMode]?.buCreatingTask ?? "Creating the task..."}
              </span>
            ) : (
              translations[langMode]?.buCreateTasks ?? "Create Tasks"
            )}
          </button>
          </Tooltip>
          <CommonButton colorIntent="success"
            onClick={handleBulkAssignSaveTask}
            variant="success"
            disabled={isCreateLoading || isCreateAssignLoading}
            tooltipText={translations[langMode].createandassingtasktofield}
            tooltipPosition="top"
            data-testid="bulk-upload-assign-save-button"
          >
            {isCreateAssignLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {translations[langMode]?.buCreatingTask ?? "Creating the task..."}
              </span>
            ) : (
              translations[langMode].CreateandAssigne
            )}
          </CommonButton>
          </>
          )}
        </div>
      </div>
    );
  }

  const panelInner = (
    <>
              <div className="flex justify-between items-center pb-0">
                <div className="flex items-center gap-2">
                  {step === 2 && (
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      aria-label={translations[langMode]?.back ?? "Back"}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                      data-testid="bulk-upload-back-button"
                    >
                      <ArrowLeft size={18} />
                    </button>
                  )}
                  <h2 className="text-l font-extrabold" data-testid="bulk-upload-title">
                    {step === 2
                      ? (translations[langMode]?.reassignTasks ?? "Reassign Tasks")
                      : isReassign
                        ? (translations[langMode]?.bulkReAssign ?? "Bulk Re-Assign")
                        : translations[langMode].BulkUpload}
                  </h2>
                </div>
                {!inline && <ModalCloseButton onClick={() => setBulkUploadOpen(false)} />}
              </div>
              {step === 1 && (
              <div className="mt-3 space-y-4 ">
                <div className="border p-3 rounded-md h-32 overflow-y-auto">
                  {accountNumbers.map((num, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 bg-gray-200 rounded mr-2 mb-2"
                    >
                      {num}
                      <button
                        onClick={() =>
                          setAccountNumbers((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        className="ml-2 text-red-500"
                      >
                        x
                      </button>
                    </span>
                  ))}
                  <textarea
                    data-testid="bulk-upload-account-textarea"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleManualEntryKeyDown}
                    onPaste={handlePasteExcel}
                    placeholder={translations[langMode].enteraccno}
                    className="w-full border-none outline-none resize-none p-2 "
                  />
                </div>
                <div className="flex justify-between items-end gap-3">
                  <div
                    className="max-w-[450px] w-full"
                    data-testid="bulk-upload-field-agent-select"
                  >
                    <label className="text-sm font-normal block mb-1">
                      {translations[langMode].selectFieldAget}
                    </label>
                    <ReactSelector
                      isMulti // This is the key prop to enable multiple selections
                      defaultValue={translations[langMode].assignTo}
                      options={agentOptions}
                      value={selectedOptions}
                      onChange={handleAgentSelection}
                      height="36px"
                      isClearable={true}
                      menuPortalTarget={document.body}
                      placeholder={translations[langMode].selectFieldAget}
                      overflow="overlay"
                      components={{ Placeholder }}
                    />
                  </div>
                  {!isReassign && (
                    <div
                      className="w-full max-w-[200px]"
                      data-testid="bulk-upload-priority-select"
                    >
                      <label className="text-sm font-normal block mb-1">
                        {translations[langMode].taskpriority}
                      </label>
                      <ReactSelector
                        dataTestId="bulk-upload-priority-selector"
                        value={priorityOptions.find((opt) => opt.value === selectedPriority) || null}
                        options={priorityOptions}
                        onChange={(selected) => setSelectedPriority(selected?.value || "")}
                        placeholder={translations[langMode].taskpriority}
                        height="36px"
                        menuPortalTarget={document.body}
                      />
                    </div>
                  )}
                  <div className="w-1/4 text-center">
                    <label className="text-sm font-normal block mb-1">
                      {translations[langMode].totalcount}
                    </label>
                    <input
                      type="text"
                      disabled
                      value={accountNumbers.length}
                      className={`flex items-center justify-center h-9 w-full border border-slate-200 bg-slate-50 rounded-lg font-semibold text-slate-700 cursor-not-allowed ${
                        langMode === "ar" || langMode === "ur" ? "text-sm" : "text-base"
                      }`}
                      data-testid="bulk-upload-total-count"
                    />
                  </div>
                </div>
              </div>
              )}

              {/* Step 2 (reassign): split entered accounts into new / existing. */}
              {step === 2 && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="bulk-upload-split">
                  {/* New accounts */}
                  <div className="border rounded-md p-3 flex flex-col justify-between" data-testid="bulk-upload-new-accounts">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h3 className="text-sm font-semibold">
                        {translations[langMode]?.newAccounts ?? "New Accounts"} ({newAccounts.length})
                      </h3>
                      <div className="w-24 shrink-0" data-testid="bulk-upload-reassign-priority-select">
                        <ReactSelector
                          dataTestId="bulk-upload-reassign-priority-selector"
                          value={shortPriorityOptions.find((opt) => opt.value === selectedPriority) || null}
                          options={shortPriorityOptions}
                          onChange={(selected) => setSelectedPriority(selected?.value || "")}
                          placeholder={translations[langMode].taskpriority}
                          height="32px"
                          isDisabled={!newAccounts.length}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (_base, state) => ({
                              cursor: state.isDisabled ? "not-allowed" : "pointer",
                            }),
                          }}
                        />
                      </div>
                    </div>
                    <div className="h-40 overflow-y-auto mb-3">
                      {newAccounts.length ? (
                        newAccounts.map((num, index) => (
                          <div
                            key={index}
                            className="flex items-center px-2 py-1.5 bg-gray-200 rounded mb-2"
                          >
                            <span className="truncate">{num}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-400">
                          {translations[langMode]?.noNewAccounts ?? "No new accounts"}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Existing accounts */}
                  <div className="border rounded-md p-3 justify-between flex flex-col" data-testid="bulk-upload-existing-accounts">
                    <h3 className="text-sm font-semibold mb-4">
                      {translations[langMode]?.existingAccounts ?? "Existing Accounts"} ({existingAccounts.length})
                    </h3>
                    <div className="h-40 overflow-y-auto mb-3">
                      {existingAccounts.length ? (
                        existingAccounts.map((item, index) => {
                          const isExpanded = expandedExisting === index;
                          return (
                            <div key={index} className="rounded bg-gray-200 mb-2 overflow-hidden">
                              <button
                                type="button"
                                onClick={() => setExpandedExisting(isExpanded ? null : index)}
                                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-left"
                                aria-expanded={isExpanded}
                              >
                                <span className="truncate">{item.accountNo}</span>
                                <span className="flex items-center gap-1 text-xs text-gray-600 shrink-0">
                                  {(item.assignees || []).length} {translations[langMode]?.assignees ?? "assignees"}
                                  <ChevronDown
                                    size={14}
                                    className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                  />
                                </span>
                              </button>
                              {isExpanded && (
                                <div className="px-3 py-1 space-y-1 bg-gray-100">
                                  {(item.assignees || []).length ? (
                                    item.assignees.map((assignee) => (
                                      <div key={assignee.assigneeId} className="text-xs text-gray-700 py-0.5">
                                        • {assignee.assigneeName}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="text-xs text-gray-400 py-0.5">
                                      {translations[langMode]?.noAssignees ?? "No assignees"}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-400">
                          {translations[langMode]?.noExistingAccounts ?? "No existing accounts"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 footer: choose to create+assign everything, or reassign the
                  already-existing accounts only. */}
              {step === 2 && (
                <div className="mt-6 flex justify-center gap-2" data-testid="bulk-upload-step2-footer">
                  <CommonButton colorIntent="danger"
                    tooltipText={translations[langMode]?.buCloseDialog ?? "Close the dialog box"}
                    tooltipPosition="top"
                    onClick={() => setBulkUploadOpen(false)}
                    variant="danger"
                    data-testid="bulk-upload-cancel-button"
                  >
                    {translations[langMode].cancle}
                  </CommonButton>
                  <CommonButton colorIntent="warning"
                    onClick={handleReassignExisting}
                    variant="primary"
                    disabled={!existingAccounts.length || isReassignLoading}
                    tooltipText={translations[langMode]?.reassignTasks ?? "Reassign existing tasks only"}
                    tooltipPosition="top"
                    className={`${isReassignLoading ? "bg-opacity-60" : "bg-opacity-100"}`}
                    data-testid="bulk-upload-reassign-existing-button"
                  >
                    {isReassignLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {translations[langMode].Processing}
                      </span>
                    ) : (
                      translations[langMode]?.reassignOnlyExisting ?? "Reassign only existing"
                    )}
                  </CommonButton>
                  <CommonButton colorIntent="success"
                    onClick={handleCreateAndAssign}
                    variant="success"
                    disabled={
                      !canCreateTask ||
                      isCreateAssignLoading ||
                      (!newAccounts.length && !existingAccounts.length)
                    }
                    tooltipText={!canCreateTask ? translations[langMode].nocreatetaskaccess : translations[langMode].createandassingtasktofield}
                    tooltipPosition="top"
                    className={`${isCreateAssignLoading ? "bg-opacity-60" : "bg-opacity-100"}`}
                    data-testid="bulk-upload-create-assign-button"
                  >
                    {isCreateAssignLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {translations[langMode]?.buCreatingTask ?? "Creating the task..."}
                      </span>
                    ) : (
                      translations[langMode].CreateandAssigne
                    )}
                  </CommonButton>
                </div>
              )}

              {step === 1 && (
              <div className="mt-6 flex justify-center gap-2">
                <CommonButton colorIntent="danger"
                  tooltipText={translations[langMode]?.buCloseDialog ?? "Close the dialog box"}
                  tooltipPosition="top"
                  onClick={() => setBulkUploadOpen(false)}
                  variant="danger"
                  data-testid="bulk-upload-cancel-button"
                >
                  {translations[langMode].cancle}
                </CommonButton>
                {isReassign ? (
                  <CommonButton colorIntent="primary"
                    tooltipText={translations[langMode]?.reassignTasks ?? "Reassign Tasks"}
                    tooltipPosition="top"
                    onClick={handleReassignSubmit}
                    variant="primary"
                    disabled={isLookupLoading}
                    className={`${isLookupLoading ? "bg-opacity-60" : "bg-opacity-100"}`}
                    data-testid="bulk-upload-reassign-submit-button"
                  >
                    {isLookupLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {translations[langMode].Processing}
                      </span>
                    ) : (
                      translations[langMode].submit
                    )}
                  </CommonButton>
                ) : (
                <CommonButton colorIntent="success"
                  tooltipText={translations[langMode].createamanualtask}
                  tooltipPosition="top"
                  onClick={handleBulkSaveTask}
                  variant="primary"
                  className={`${isCreateLoading ? "bg-opacity-60" : "bg-opacity-100"}`}
                  data-testid="bulk-upload-save-button"
                >
                  {isCreateLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {translations[langMode]?.buCreatingTask ?? "Creating the task..."}
                    </span>
                  ) : (
                    translations[langMode].create
                  )}
                </CommonButton>
                )}
                {!isReassign && (
                <CommonButton colorIntent="success"
                  onClick={handleBulkAssignSaveTask}
                  variant="success"
                  tooltipText={translations[langMode].createandassingtasktofield}
                  tooltipPosition="top"
                  className={`${isCreateAssignLoading ? "bg-opacity-60" : "bg-opacity-100"}`}
                  data-testid="bulk-upload-assign-save-button"
                >
                  {isCreateAssignLoading ? translations[langMode].Processing : translations[langMode].CreateandAssigne}
                </CommonButton>
                )}
              </div>
              )}
    </>
  );

  // Inline (tab) reassign view — styled like the inline Bulk Task Upload (card layout).
  if (inline) {
    return (
      <div className="max-w-[1400px] w-full mx-auto px-5 pt-6 pb-6" data-testid="bulk-upload-inline-reassign">
        {step === 1 ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <section className="rounded-xl border border-slate-100 bg-white p-5 flex flex-col justify-between">
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#236DB4] text-[10px] font-bold text-white">1</span>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">{translations[langMode]?.buAccountNumbers ?? "Account Numbers"}</h3>
                  </div>
                  <fieldset className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 focus-within:border-[#236DB4] focus-within:ring-2 focus-within:ring-[#236DB4]/15 transition-all duration-200">
                    <legend className="px-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {translations[langMode]?.buAccountNo ?? "Account No"} <span className="text-red-500">*</span>
                    </legend>
                    <div className="flex flex-wrap gap-2 items-start content-start min-h-[140px] pb-1 overflow-y-auto">
                      {accountNumbers.map((num, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 border border-slate-200 rounded-md text-sm text-slate-700 font-semibold shadow-sm"
                        >
                          {num}
                          <button
                            type="button"
                            onClick={() => setAccountNumbers((prev) => prev.filter((_, i) => i !== index))}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        placeholder={accountNumbers.length === 0 ? (translations[langMode]?.buTypeAccountPlaceholder ?? "Type account number and press Enter") : ""}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleManualEntryKeyDown}
                        onPaste={handlePasteExcel}
                        onBlur={() => {
                          const val = inputValue.trim();
                          if (val && !accountNumbers.includes(val)) {
                            setAccountNumbers((prev) => [...prev, val]);
                            setInputValue("");
                          }
                        }}
                        className="flex-1 min-w-[150px] bg-transparent border-none outline-none focus:ring-0 p-1 text-sm text-slate-700"
                        data-testid="bulk-upload-account-textarea"
                      />
                    </div>
                  </fieldset>
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                  <span>{translations[langMode]?.buTypeAndEnterHint ?? "Type and press Enter, or paste a list of accounts."}</span>
                  <span className="font-medium text-slate-500">{(translations[langMode]?.buDetected ?? "{n} detected").replace("{n}", accountNumbers.length)}</span>
                </div>
              </section>

              <section className="rounded-xl border border-slate-100 bg-white p-5 flex flex-col justify-between">
                <div>
                  <div className="mb-4 flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#236DB4] text-[10px] font-bold text-white">2</span>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">{translations[langMode]?.buAssignment ?? "Assignment"}</h3>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div data-testid="bulk-upload-field-agent-select">
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        {translations[langMode].selectFieldAget}
                      </label>
                      <ReactSelector
                        isMulti
                        options={agentOptions}
                        value={selectedOptions}
                        onChange={handleAgentSelection}
                        height="38px"
                        isClearable
                        menuPortalTarget={document.body}
                        placeholder={translations[langMode].selectFieldAget}
                        components={{ Placeholder }}
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        {translations[langMode].totalcount}
                      </label>
                      <input
                        type="text"
                        disabled
                        value={accountNumbers.length}
                        className="flex h-[38px] w-full items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-700 cursor-not-allowed"
                        data-testid="bulk-upload-total-count"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-8 flex items-center justify-end gap-3 border-t border-slate-100 bg-white px-5 py-4">
              <CommonButton colorIntent="danger"
                onClick={() => setBulkUploadOpen(false)}
                variant="danger"
                tooltipText={translations[langMode].gobacktheprivious || "Cancel and go back"}
                tooltipPosition="top"
                data-testid="bulk-upload-cancel-button"
              >
                {translations[langMode].cancel || "Cancel"}
              </CommonButton>
              <Tooltip text={translations[langMode]?.reassignTasks ?? "Reassign Tasks"} position="top">
              <button data-button-intent="primary"
                type="button"
                onClick={handleReassignSubmit}
                disabled={isLookupLoading}
                className="h-9 rounded-lg bg-[#236DB4] px-5 text-sm font-semibold text-white hover:bg-[#1d5992] disabled:opacity-60"
                data-testid="bulk-upload-reassign-submit-button"
              >
                {isLookupLoading ? translations[langMode].Processing : translations[langMode].submit}
              </button>
              </Tooltip>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              {/* New accounts */}
              <section className="rounded-xl border border-slate-100 bg-white p-5 flex flex-col" data-testid="bulk-upload-new-accounts">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#236DB4] text-[10px] font-bold text-white">1</span>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      {translations[langMode]?.newAccounts ?? "New Accounts"} ({newAccounts.length})
                    </h3>
                  </div>
                  <div className="w-28 shrink-0" data-testid="bulk-upload-reassign-priority-select">
                    <ReactSelector
                      dataTestId="bulk-upload-reassign-priority-selector"
                      value={shortPriorityOptions.find((opt) => opt.value === selectedPriority) || null}
                      options={shortPriorityOptions}
                      onChange={(selected) => setSelectedPriority(selected?.value || "")}
                      placeholder={translations[langMode].taskpriority}
                      height="32px"
                      isDisabled={!newAccounts.length}
                      menuPortalTarget={document.body}
                      styles={{ control: (_base, state) => ({ cursor: state.isDisabled ? "not-allowed" : "pointer" }) }}
                    />
                  </div>
                </div>
                <div className="h-48 overflow-y-auto">
                  {newAccounts.length ? (
                    newAccounts.map((num, index) => (
                      <div key={index} className="flex items-center px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-md mb-2 text-sm text-slate-700">
                        <span className="truncate">{num}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400">{translations[langMode]?.noNewAccounts ?? "No new accounts"}</p>
                  )}
                </div>
              </section>

              {/* Existing accounts */}
              <section className="rounded-xl border border-slate-100 bg-white p-5 flex flex-col" data-testid="bulk-upload-existing-accounts">
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#236DB4] text-[10px] font-bold text-white">2</span>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    {translations[langMode]?.existingAccounts ?? "Existing Accounts"} ({existingAccounts.length})
                  </h3>
                </div>
                <div className="h-48 overflow-y-auto">
                  {existingAccounts.length ? (
                    existingAccounts.map((item, index) => {
                      const isExpanded = expandedExisting === index;
                      return (
                        <div key={index} className="rounded-md border border-slate-200 bg-slate-50 mb-2 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setExpandedExisting(isExpanded ? null : index)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-slate-700"
                            aria-expanded={isExpanded}
                          >
                            <span className="truncate">{item.accountNo}</span>
                            <span className="flex items-center gap-1 text-xs text-gray-600 shrink-0">
                              {(item.assignees || []).length} {translations[langMode]?.assignees ?? "assignees"}
                              <ChevronDown size={14} className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="px-3 py-1 space-y-1 bg-white border-t border-slate-200">
                              {(item.assignees || []).length ? (
                                item.assignees.map((assignee) => (
                                  <div key={assignee.assigneeId} className="text-xs text-gray-700 py-0.5">
                                    • {assignee.assigneeName}
                                  </div>
                                ))
                              ) : (
                                <div className="text-xs text-gray-400 py-0.5">{translations[langMode]?.noAssignees ?? "No assignees"}</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-400">{translations[langMode]?.noExistingAccounts ?? "No existing accounts"}</p>
                  )}
                </div>
              </section>
            </div>

            <div className="mt-8 flex items-center justify-end gap-3 border-t border-slate-100 bg-white px-5 py-4">
              <CommonButton colorIntent="primary"
                onClick={() => setStep(1)}
                variant="danger"
                tooltipText={translations[langMode]?.back ?? "Back"}
                tooltipPosition="top"
                data-testid="bulk-upload-back-button"
              >
                {translations[langMode]?.back ?? "Back"}
              </CommonButton>
              <CommonButton colorIntent="warning"
                onClick={handleReassignExisting}
                variant="primary"
                disabled={!existingAccounts.length || isReassignLoading}
                tooltipText={translations[langMode]?.reassignTasks ?? "Reassign existing tasks only"}
                tooltipPosition="top"
                data-testid="bulk-upload-reassign-existing-button"
              >
                {isReassignLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {translations[langMode].Processing}
                  </span>
                ) : (
                  translations[langMode]?.reassignOnlyExisting ?? "Reassign only existing"
                )}
              </CommonButton>
              <CommonButton colorIntent="success"
                onClick={handleCreateAndAssign}
                variant="success"
                disabled={!canCreateTask || isCreateAssignLoading || (!newAccounts.length && !existingAccounts.length)}
                tooltipText={!canCreateTask ? translations[langMode].nocreatetaskaccess : translations[langMode].createandassingtasktofield}
                tooltipPosition="top"
                data-testid="bulk-upload-create-assign-button"
              >
                {isCreateAssignLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {translations[langMode]?.buCreatingTask ?? "Creating the task..."}
                  </span>
                ) : (
                  translations[langMode].CreateandAssigne
                )}
              </CommonButton>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <Transition.Root show={bulkUploadOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20"
        initialFocus={cancelButtonRef}
        onClose={() => { }}
      >
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[1px] transition-opacity" />
        <div className="fixed inset-0 w-screen overflow-y-auto ">
          <div className="flex min-h-full items-center justify-center p-1">
            <Dialog.Panel className="w-full max-w-2xl bg-white rounded-lg shadow-xl p-4 pt-2" data-testid="bulk-upload-modal">
              {panelInner}
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

